import { useContext, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AuthContext } from "../Contexts/AuthContext";
import {
  BadgeHelp,
  Headset,
  House,
  LogOut,
  Menu,
  User,
  UserRound,
  X,
} from "lucide-react";
import useCurrentUsuario from "../hooks/useCurrentUsuario";
import { getStatusClasses, getSubscriptionInfo } from "../utils/subscription";

function NavBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, isAdmin, user, autenticado } = useContext(AuthContext);
  const [mostrar, setMostrar] = useState(false);
  const { usuario: usuarioAtual, loading: loadingSubscricao } =
    useCurrentUsuario(!isAdmin && autenticado);
  const subscription = getSubscriptionInfo(usuarioAtual || {});
  const subscriptionClasses = getStatusClasses(subscription.statusTone);

  const homePath = isAdmin ? "/admin" : "/home";

  function sair() {
    logout();
    navigate("/");
  }

  function navLinkClasses(path) {
    return `group flex items-center justify-between gap-3 rounded-md border px-4 py-3 transition duration-200 ${
      location.pathname === path
        ? "bg-white text-blue-600"
        : "border-blue-300 text-white hover:bg-blue-600"
    }`;
  }

  function desktopLinkClasses(path) {
    return `rounded-md border px-4 py-2 transition ${
      location.pathname === path
        ? "bg-white text-blue-600"
        : "border-blue-200 text-white hover:bg-white hover:text-blue-600"
    }`;
  }

  return (
    <div className="mx-auto w-full bg-blue-500 font-questrial text-gray-100 shadow-sm">
      <div
        className={`fixed inset-0 z-50 flex w-full flex-col bg-blue-500 px-5 py-4 transition-transform duration-300 ease-in-out ${
          mostrar ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between">
          <img width={90} src="../../images/logo.png" alt="Logotipo" />
          <button
            type="button"
            onClick={() => setMostrar(false)}
            className="rounded-md p-2 text-white hover:bg-blue-600"
          >
            <X size={28} />
          </button>
        </div>

        <div className="mt-6 flex flex-col gap-3">
          <Link to={homePath} className={navLinkClasses(homePath)}>
            <House size={20} />
            <span className="flex-1 text-center font-semibold">Inicio</span>
          </Link>

          {!isAdmin && (
            <>
              <Link to="/quemsomos" className={navLinkClasses("/quemsomos")}>
                <Headset size={20} />
                <span className="flex-1 text-center font-semibold">Sobre Nos</span>
              </Link>
              <Link to="/ajuda" className={navLinkClasses("/ajuda")}>
                <BadgeHelp size={20} />
                <span className="flex-1 text-center font-semibold">Ajuda</span>
              </Link>
            </>
          )}

          <button type="button" onClick={sair} className={navLinkClasses("/logout")}>
            <LogOut size={20} />
            <span className="flex-1 text-center font-semibold">Sair</span>
          </button>
        </div>
      </div>

      <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4">
        <div className="flex items-center gap-3">
          <img
            className="w-[68px] sm:w-[100px]"
            src="../../images/logo.png"
            alt="Logotipo"
          />
          <button
            type="button"
            className="rounded-md p-1 text-white hover:bg-blue-600 md:hidden"
            onClick={() => setMostrar(true)}
          >
            <Menu size={34} />
          </button>
        </div>

        {isAdmin ? (
          <h1 className="hidden text-3xl font-semibold md:block">Administrador</h1>
        ) : (
          <div className="hidden items-center gap-3 md:flex">
            <Link to="/home" className={desktopLinkClasses("/home")}>
              Inicio
            </Link>
            <Link to="/quemsomos" className={desktopLinkClasses("/quemsomos")}>
              Nos
            </Link>
            <Link to="/ajuda" className={desktopLinkClasses("/ajuda")}>
              Ajuda
            </Link>
          </div>
        )}

        <div className="flex flex-col items-end gap-2">
          <div className="flex items-center gap-3">
            <div className="hidden text-sm md:block">
              <span>Ola, </span>
              <span className="font-semibold">{user}</span>
            </div>
            {autenticado && (
              <button
                type="button"
                onClick={sair}
                className="hidden rounded-md p-1 hover:bg-blue-600 md:block"
              >
                <LogOut size={20} />
              </button>
            )}
            {!isAdmin && (
              <button
                type="button"
                onClick={() => navigate("/minhaconta")}
                className="hidden rounded-md p-1 hover:bg-blue-600 md:block"
              >
                <User size={20} />
              </button>
            )}
          </div>

          <div className="flex items-center gap-2 md:hidden">
            <span className="text-xs">
              Ola, <strong>{user}</strong>
            </span>
            {!isAdmin && (
              <UserRound
                className="cursor-pointer hover:text-blue-100"
                size={18}
                onClick={() => navigate("/minhaconta")}
              />
            )}
          </div>

          {!isAdmin && autenticado && (
            <span
              className={`inline-flex max-w-[240px] items-center rounded-md border px-2 py-1 text-xs font-semibold ${subscriptionClasses}`}
            >
              {loadingSubscricao
                ? "A confirmar..."
                : subscription.isActive
                  ? `${subscription.planLabel} Activo`
                  : subscription.statusLabel}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export default NavBar;
